import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import LoadingSpinner from '../../../components/LoadingSpinner'
import GroupLeaderBottomNav from '../../../components/GroupLeaderBottomNav'
import { QRCodeSVG } from 'qrcode.react'
import { 
  QrCode, 
  Download, 
  Bell, 
  UserCircle,
  Buildings,
  Info,
  Warning,
  Wallet
} from '@phosphor-icons/react'

export default function GroupLeaderCode() {
  const navigate = useNavigate()
  const [userData, setUserData] = useState(null)
  const [orgData, setOrgData] = useState(null)
  const [hierarchyInfo, setHierarchyInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [addingToWallet, setAddingToWallet] = useState(false)

  // Device detection
  const userAgent = navigator.userAgent.toLowerCase()
  const isIOS = /iphone|ipad|ipod/.test(userAgent)
  const isAndroid = /android/.test(userAgent)
  const isMobile = isIOS || isAndroid
  const isDesktop = !isMobile

  useEffect(() => {
    loadUserData()
  }, [])

  const loadUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      const { data: userDataResult } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single()
      
      setUserData(userDataResult)

      const { data: orgDataResult } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', userDataResult.org_id)
        .single()

      setOrgData(orgDataResult)

      try {
        const { data: groupMemberships } = await supabase
          .from('group_members')
          .select(`
            group_id,
            groups:group_id (
              id,
              name,
              depth,
              parent_id
            )
          `)
          .eq('user_id', user.id)

        if (groupMemberships && groupMemberships.length > 0) {
          const { data: allGroups } = await supabase
            .from('groups')
            .select('*')
            .eq('org_id', userDataResult.org_id)

          const hierarchy = { team: null, group: null, subgroup: null }
          
          groupMemberships.forEach(membership => {
            const group = membership.groups
            if (!group) return

            if (group.depth === 1) {
              hierarchy.team = group
            } else if (group.depth === 2) {
              hierarchy.group = group
              const parentTeam = allGroups.find(g => g.id === group.parent_id)
              if (parentTeam) hierarchy.team = parentTeam
            } else if (group.depth === 3) {
              hierarchy.subgroup = group
              const parentGroup = allGroups.find(g => g.id === group.parent_id)
              if (parentGroup) {
                hierarchy.group = parentGroup
                const grandparentTeam = allGroups.find(g => g.id === parentGroup.parent_id)
                if (grandparentTeam) hierarchy.team = grandparentTeam
              }
            }
          })

          setHierarchyInfo(hierarchy)
        }
      } catch (error) {
        console.log('Error loading hierarchy:', error)
      }

    } catch (error) {
      console.error('Load user data error:', error)
    } finally {
      setLoading(false)
    }
  }

  const getRoleDisplay = () => {
    if (!userData) return 'Member'
    
    if (userData.role === 'org_admin') return 'Organization Admin'
    if (userData.leadership_level === 'team' && hierarchyInfo?.team) {
      return `Team Leader - ${hierarchyInfo.team.name}`
    }
    if (userData.leadership_level === 'group' && hierarchyInfo?.group) {
      return `Group Leader - ${hierarchyInfo.group.name}`
    }
    if (userData.leadership_level === 'subgroup' && hierarchyInfo?.subgroup) {
      return `Subgroup Leader - ${hierarchyInfo.subgroup.name}`
    }
    
    if (hierarchyInfo?.subgroup) return `Member - ${hierarchyInfo.subgroup.name}`
    if (hierarchyInfo?.group) return `Member - ${hierarchyInfo.group.name}`
    if (hierarchyInfo?.team) return `Member - ${hierarchyInfo.team.name}`
    
    return 'Member'
  }

  const getDepartmentDisplay = () => {
    if (!userData) return 'New Member'
    
    // For leaders, show their leadership title with group name
    if (userData.leadership_level === 'team' && hierarchyInfo?.team) {
      return `Lead - ${hierarchyInfo.team.name}`
    }
    if (userData.leadership_level === 'group' && hierarchyInfo?.group) {
      return `Head - ${hierarchyInfo.group.name}`
    }
    if (userData.leadership_level === 'subgroup' && hierarchyInfo?.subgroup) {
      return `H.O.D - ${hierarchyInfo.subgroup.name}`
    }
    
    // For regular members, show "Member - GroupName"
    if (hierarchyInfo?.subgroup) return `Member - ${hierarchyInfo.subgroup.name}`
    if (hierarchyInfo?.group) return `Member - ${hierarchyInfo.group.name}`
    if (hierarchyInfo?.team) return `Member - ${hierarchyInfo.team.name}`
    
    return 'New Member'
  }

  const downloadQRCode = () => {
    const svg = document.getElementById('qr-code-svg')
    if (svg) {
      const svgData = new XMLSerializer().serializeToString(svg)
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()
      
      canvas.width = 300
      canvas.height = 300
      
      img.onload = () => {
        ctx.fillStyle = 'white'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(img, 0, 0)
        
        const url = canvas.toDataURL('image/png')
        const link = document.createElement('a')
        link.download = `${userData.name}-qr-code.png`
        link.href = url
        link.click()
      }
      
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
    }
  }

  const handleAddToGoogleWallet = async () => {
    try {
      setAddingToWallet(true)
      
      const requestBody = {
        qrCodeData: userData?.id,
        userName: userData?.name || 'Member',
        userRole: getRoleDisplay(),
        organizationName: orgData?.name || "Organization",
        userId: userData?.id,
        logoUrl: orgData?.logo_url,
      }
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-google-wallet-pass`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify(requestBody),
        }
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Server error: ${response.status}`)
      }

      const data = await response.json()

      if (data.success && data.saveUrl) {
        if (data.isUpdate) {
          alert('Your wallet pass has been updated!')
        }
        window.location.href = data.saveUrl
      } else {
        throw new Error(data.error || 'Failed to create wallet pass')
      }
    } catch (error) {
      console.error('Error:', error)
      alert(`Failed to add to Google Wallet: ${error.message}`)
    } finally {
      setAddingToWallet(false)
    }
  }

  const handleAddToAppleWallet = async () => {
    try {
      setAddingToWallet(true)
      
      const requestBody = {
        qrCodeData: userData?.id,
        userName: userData?.name || 'Member',
        userRole: getRoleDisplay(),
        organizationName: orgData?.name || "Organization",
        userId: userData?.id,
        logoUrl: orgData?.logo_url,
      }
      
      const response = await fetch('/api/apple-wallet-pass', {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || `Server error: ${response.status}`)
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${userData.name}-attendance.pkpass`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
    } catch (error) {
      console.error('Error:', error)
      alert(`Failed to add to Apple Wallet: ${error.message}`)
    } finally {
      setAddingToWallet(false)
    }
  }

  const addToWallet = async () => {
    if (isDesktop) {
      alert('Please open this page on your iPhone or Android phone to add to wallet.\n\nYou can:\n• Scan the QR code with your phone\n• Email/text yourself the link\n• Use the Download button to save the QR code')
      return
    }

    if (isAndroid) {
      await handleAddToGoogleWallet()
    } else if (isIOS) {
      await handleAddToAppleWallet()
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading..." />
  }

  return (
    <>
      <div className="bg-white min-h-screen pb-20">
        {/* Header */}
        <div className="px-6 pt-10 pb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-[#1E293B]">My QR Code</h1>
          <button className="w-10 h-10 flex items-center justify-center bg-slate-50 border border-slate-200 rounded-full">
            <Bell size={20} weight="fill" className="text-[#64748B]" />
          </button>
        </div>

        <div className="px-6 space-y-6">
          {/* Blue Card with Pattern */}
          <div 
            className="rounded-[20px] py-6 px-5 relative overflow-hidden"
            style={{
              backgroundColor: '#2248B2'
            }}
          >
            {/* Pattern overlay - separate layer */}
            <div 
              className="absolute inset-0 opacity-80"
              style={{
                backgroundImage: 'url(/Line4.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            ></div>

            {/* Content above pattern */}
            <div className="relative z-10">
              {/* Top Row: Icon + Organization Name */}
              <div className="flex items-center justify-between mb-10">
                {/* Organization Logo/Icon */}
                <div className="flex-shrink-0">
                  {orgData?.logo_url ? (
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center p-1">
                      <img 
                        src={orgData.logo_url} 
                        alt={orgData.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Buildings size={24} weight="fill" className="text-white" />
                    </div>
                  )}
                </div>

                {/* Organization Name */}
                <p className="text-sm font-semibold text-[#ADC2FF]">{orgData?.name || 'Organization'}</p>
              </div>

              {/* Bottom: User Info */}
              <div>
                <h2 className="text-xl font-semibold text-[#EAF0FF] mb-1">{userData?.name}</h2>
                <p className="text-sm font-regular text-[#ADC2FF]">{getDepartmentDisplay()}</p>
              </div>
            </div>
          </div>

          {/* QR Code Container */}
          {userData?.qr_signature ? (
            <div className="bg-white rounded-[20px] border border-slate-200 p-0.5 flex items-center justify-center shadow-sm">
              <QRCodeSVG
                id="qr-code-svg"
                value={userData.qr_signature}
                size={360}
                level="H"
                includeMargin={true}
              />
            </div>
          ) : (
            <div className="bg-white rounded-[20px] border border-slate-200 p-6 flex flex-col items-center justify-center shadow-sm">
              <div className="w-64 h-64 flex flex-col items-center justify-center">
                <QrCode size={64} weight="light" className="text-slate-400 mb-3" />
                <p className="text-slate-500 text-sm">No QR code available</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {userData?.qr_signature && (
            <div className="space-y-3">
              <button
                onClick={addToWallet}
                disabled={addingToWallet}
                className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-[10px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isIOS 
                    ? 'bg-black hover:bg-gray-900 text-white' 
                    : isAndroid 
                      ? 'bg-white hover:bg-gray-50 text-slate-900 border border-slate-300' 
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300'
                }`}
              >
                {addingToWallet ? (
                  <>
                    <Wallet size={20} weight="regular" />
                    <span className="text-base">Adding to Wallet...</span>
                  </>
                ) : isIOS ? (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                    <span className="text-base">Add to Apple Wallet</span>
                  </>
                ) : isAndroid ? (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    <span className="text-base">Add to Google Wallet</span>
                  </>
                ) : (
                  <>
                    <Wallet size={20} weight="regular" />
                    <span className="text-base">Add to Wallet (Mobile Only)</span>
                  </>
                )}
              </button>

              <button
                onClick={downloadQRCode}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-[10px] font-medium bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 transition-colors"
              >
                <Download size={20} weight="fill" />
                <span className="text-base">Download QR Code</span>
              </button>
            </div>
          )}
        </div>
      </div>
      
      <GroupLeaderBottomNav />
    </>
  )
}